import React, { useEffect, useState, createContext, useContext } from 'react';
import { useUser } from './UserContext';
export interface PackageDeduction {
  id: string;
  date: string;
  orderId: string;
  reportType: string;
  reportTitle: string;
  source: 'ai' | 'astrologer';
  astrologerName?: string;
}
export interface ReportPackage {
  id: string;
  type: 20 | 50 | 100;
  title: string;
  ownerId: string;
  ownerType: 'user' | 'astrologer';
  totalReports: number;
  usedReports: number;
  remainingReports: number;
  purchaseDate: string;
  purchasePrice: number;
  pricePerReport: number;
  discount: number;
  discountReason?: string;
  status: 'active' | 'exhausted' | 'frozen' | 'cancelled';
  deductions: PackageDeduction[];
}
export const BASE_PRICES = {
  20: 76000,
  50: 180000,
  100: 315000
};
interface PackagesContextType {
  packages: ReportPackage[];
  activePackages: ReportPackage[];
  totalRemaining: number;
  totalUsed: number;
  canPayFromPackage: boolean;
  addPackage: (type: 20 | 50 | 100, ownerType: 'user' | 'astrologer', discount?: number, discountReason?: string) => void;
  useReport: (packageId: string, deduction: Omit<PackageDeduction, 'id' | 'date'>) => boolean;
  getOldestActivePackage: () => ReportPackage | null;
  getPackagePrice: (type: 20 | 50 | 100, discount: number) => number;
  freezePackage: (id: string) => void;
  cancelPackage: (id: string) => void;
}
const PackagesContext = createContext<PackagesContextType | undefined>(undefined);
const MOCK_PACKAGES: ReportPackage[] = [{
  id: 'pkg-1',
  type: 20,
  title: '20 отчётов',
  ownerId: 'user-1',
  ownerType: 'user',
  totalReports: 20,
  usedReports: 8,
  remainingReports: 12,
  purchaseDate: '2026-01-15T10:00:00Z',
  purchasePrice: 76000,
  pricePerReport: 3800,
  discount: 0,
  status: 'active',
  deductions: [{
    id: 'ded-1',
    date: '2026-01-16T14:30:00Z',
    orderId: 'ord-101',
    reportType: 'natal',
    reportTitle: 'Натальная карта',
    source: 'ai'
  }, {
    id: 'ded-2',
    date: '2026-02-05T09:15:00Z',
    orderId: 'ord-105',
    reportType: 'solar',
    reportTitle: 'Соляр на 2026 год',
    source: 'astrologer',
    astrologerName: 'Елена Смирнова'
  }]
}, {
  id: 'pkg-2',
  type: 50,
  title: '50 отчётов',
  ownerId: 'user-1',
  ownerType: 'user',
  totalReports: 50,
  usedReports: 3,
  remainingReports: 47,
  purchaseDate: '2026-03-01T11:20:00Z',
  purchasePrice: 180000,
  pricePerReport: 3600,
  discount: 0,
  status: 'active',
  deductions: [{
    id: 'ded-3',
    date: '2026-03-05T16:45:00Z',
    orderId: 'ord-112',
    reportType: 'compatibility',
    reportTitle: 'Совместимость с партнером',
    source: 'ai'
  }]
}];
export function PackagesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useUser();
  const [packages, setPackages] = useState<ReportPackage[]>(MOCK_PACKAGES);
  // Filter packages by current user
  const userPackages = packages.filter(p => !user || p.ownerId === user.id);
  const activePackages = userPackages.filter(p => p.status === 'active' && p.remainingReports > 0);
  const totalRemaining = activePackages.reduce((sum, p) => sum + p.remainingReports, 0);
  const totalUsed = userPackages.reduce((sum, p) => sum + p.usedReports, 0);
  const canPayFromPackage = totalRemaining > 0;
  const getPackagePrice = (type: 20 | 50 | 100, discount: number) => {
    const basePrice = BASE_PRICES[type];
    return basePrice * (1 - discount / 100);
  };
  const addPackage = (type: 20 | 50 | 100, ownerType: 'user' | 'astrologer', discount = 0, discountReason?: string) => {
    if (!user) return;
    const purchasePrice = getPackagePrice(type, discount);
    const newPackage: ReportPackage = {
      id: `pkg-${Date.now()}`,
      type,
      title: `${type} отчётов`,
      ownerId: user.id,
      ownerType,
      totalReports: type,
      usedReports: 0,
      remainingReports: type,
      purchaseDate: new Date().toISOString(),
      purchasePrice,
      pricePerReport: Math.round(purchasePrice / type),
      discount,
      discountReason,
      status: 'active',
      deductions: []
    };
    setPackages(prev => [newPackage, ...prev]);
  };
  const useReport = (packageId: string, deduction: Omit<PackageDeduction, 'id' | 'date'>) => {
    let success = false;
    setPackages(prev => prev.map(pkg => {
      if (pkg.id === packageId && pkg.status === 'active' && pkg.remainingReports > 0) {
        success = true;
        const newRemaining = pkg.remainingReports - 1;
        return {
          ...pkg,
          usedReports: pkg.usedReports + 1,
          remainingReports: newRemaining,
          status: newRemaining === 0 ? 'exhausted' : 'active',
          deductions: [{
            ...deduction,
            id: `ded-${Date.now()}`,
            date: new Date().toISOString()
          }, ...pkg.deductions]
        };
      }
      return pkg;
    }));
    return success;
  };
  const getOldestActivePackage = () => {
    if (activePackages.length === 0) return null;
    // Sort by purchase date (oldest first)
    return [...activePackages].sort((a, b) => new Date(a.purchaseDate).getTime() - new Date(b.purchaseDate).getTime())[0];
  };
  const freezePackage = (id: string) => {
    setPackages(prev => prev.map(pkg => pkg.id === id ? {
      ...pkg,
      status: 'frozen'
    } : pkg));
  };
  const cancelPackage = (id: string) => {
    setPackages(prev => prev.map(pkg => pkg.id === id ? {
      ...pkg,
      status: 'cancelled'
    } : pkg));
  };
  return <PackagesContext.Provider value={{
    packages: userPackages,
    activePackages,
    totalRemaining,
    totalUsed,
    canPayFromPackage,
    addPackage,
    useReport,
    getOldestActivePackage,
    getPackagePrice,
    freezePackage,
    cancelPackage
  }} data-id="element-20747">
      {children}
    </PackagesContext.Provider>;
}
export function usePackages() {
  const context = useContext(PackagesContext);
  if (context === undefined) {
    throw new Error('usePackages must be used within a PackagesProvider');
  }
  return context;
}